import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client";

export type Client = {
  id: number;
  nom: string;
  email: string;
  telephone: string;
  adresse: string;
  communeId: number | null;
  communeNom: string;
  leadId: number | null;
  notes: string;
  createdAt: string;
};

type Commune = { id: number; nom: string };

export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: () => api.get<{ clients: Client[] }>("/api/clients"),
  });
}

export function useCommunes() {
  return useQuery({
    queryKey: ["contenu", "communes"],
    queryFn: () => api.get<{ communes: Commune[] }>("/api/contenu/communes"),
  });
}

export function Clients() {
  const client = useQueryClient();
  const clients = useClients();
  const communes = useCommunes();
  const [ouvert, setOuvert] = useState(false);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const creer = useMutation({
    mutationFn: (champs: Record<string, unknown>) =>
      api.post<{ client: Client }>("/api/clients", champs),
    onSuccess: () => {
      setErreurs({});
      setOuvert(false);
      client.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (erreur: Error & { erreurs?: Record<string, string> }) => {
      // L'API rend une erreur par champ ; les afficher sous le bon champ évite
      // de laisser l'utilisateur chercher ce qui ne va pas.
      setErreurs(erreur.erreurs ?? { _global: erreur.message });
    },
  });

  function soumettre(evenement: React.FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    const donnees = new FormData(evenement.currentTarget);
    const communeId = donnees.get("communeId");

    creer.mutate({
      nom: donnees.get("nom"),
      email: donnees.get("email"),
      telephone: donnees.get("telephone"),
      adresse: donnees.get("adresse"),
      communeId: communeId === "" ? null : Number(communeId),
      notes: donnees.get("notes"),
    });
  }

  const liste = clients.data?.clients ?? [];

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
        spacing={2}
      >
        <Typography variant="h5" component="h1">
          Clients
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOuvert(true)}>
          Nouveau client
        </Button>
      </Stack>

      {clients.isPending ? (
        <LinearProgress />
      ) : liste.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Aucun client. Une demande de devis peut être convertie en client depuis sa fiche.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Commune</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {liste.map((ligne) => (
                <TableRow key={ligne.id} hover>
                  <TableCell>{ligne.nom}</TableCell>
                  <TableCell>{ligne.communeNom || "—"}</TableCell>
                  <TableCell>
                    {/* Le suivi se fait au téléphone, souvent depuis un chantier. */}
                    {ligne.telephone ? (
                      <Link href={`tel:${ligne.telephone}`}>{ligne.telephone}</Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {ligne.email ? <Link href={`mailto:${ligne.email}`}>{ligne.email}</Link> : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={ouvert} onClose={() => setOuvert(false)} fullWidth maxWidth="sm">
        <form onSubmit={soumettre}>
          <DialogTitle>Nouveau client</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {erreurs._global && <Alert severity="error">{erreurs._global}</Alert>}
              <TextField
                name="nom"
                label="Nom"
                required
                fullWidth
                error={Boolean(erreurs.nom)}
                helperText={erreurs.nom}
              />
              <TextField
                name="telephone"
                label="Téléphone"
                fullWidth
                error={Boolean(erreurs.telephone)}
                helperText={erreurs.telephone ?? "Facultatif, normalisé automatiquement"}
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                fullWidth
                error={Boolean(erreurs.email)}
                helperText={erreurs.email}
              />
              <TextField name="adresse" label="Adresse" fullWidth />
              <TextField name="communeId" label="Commune" select defaultValue="" fullWidth>
                <MenuItem value="">Non précisée</MenuItem>
                {communes.data?.communes.map((commune) => (
                  <MenuItem key={commune.id} value={commune.id}>
                    {commune.nom}
                  </MenuItem>
                ))}
              </TextField>
              <TextField name="notes" label="Notes" multiline rows={3} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOuvert(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={creer.isPending}>
              {creer.isPending ? "Enregistrement…" : "Créer"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
}
